import { useState, useEffect } from "react";
import { Users, Loader2, AlertCircle, Info } from "lucide-react";

interface AttributedSentence {
  sentence_id: number;
  text: string;
  speaker: string;
  confidence: number;
  flags: string[];
  char_start: number;
  char_end: number;
}

interface AttributionResult {
  sentences: AttributedSentence[];
  reconstructedTranscript: string;
  summary: {
    totalSentences: number;
    speakerTotals: Record<string, number>;
    escalationRate: number;
    needsHumanReviewCount: number;
  };
}

interface SpeakerAttributionViewerProps {
  attributionStatus?: "processing" | "done" | "failed";
  attributionJson?: string;
  autoAcceptThreshold?: number;
}

const SPEAKER_COLORS: Record<string, { label: string; badge: string; bubble: string; filterActive: string; filterInactive: string }> = {
  SPEAKER_A: {
    label: "Clinician",
    badge: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800",
    bubble: "bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-400 dark:border-blue-600",
    filterActive: "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 ring-2 ring-blue-400 dark:ring-blue-600",
    filterInactive: "bg-transparent text-muted-foreground border-border hover:bg-blue-50 dark:hover:bg-blue-950/20 hover:text-blue-700 dark:hover:text-blue-300 hover:border-blue-200 dark:hover:border-blue-800",
  },
  SPEAKER_B: {
    label: "Patient",
    badge: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800",
    bubble: "bg-green-50 dark:bg-green-950/30 border-l-4 border-green-400 dark:border-green-600",
    filterActive: "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700 ring-2 ring-green-400 dark:ring-green-600",
    filterInactive: "bg-transparent text-muted-foreground border-border hover:bg-green-50 dark:hover:bg-green-950/20 hover:text-green-700 dark:hover:text-green-300 hover:border-green-200 dark:hover:border-green-800",
  },
  AMBIGUOUS: {
    label: "Unknown",
    badge: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
    bubble: "bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-400 dark:border-amber-600",
    filterActive: "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700 ring-2 ring-amber-400 dark:ring-amber-600",
    filterInactive: "bg-transparent text-muted-foreground border-border hover:bg-amber-50 dark:hover:bg-amber-950/20 hover:text-amber-700 dark:hover:text-amber-300 hover:border-amber-200 dark:hover:border-amber-800",
  },
};

function getSpeakerStyle(speaker: string) {
  return SPEAKER_COLORS[speaker] ?? {
    label: speaker,
    badge: "bg-muted text-muted-foreground border-border",
    bubble: "bg-muted/30 border-l-4 border-muted-foreground/40",
    filterActive: "bg-muted text-foreground border-border ring-2 ring-muted-foreground/40",
    filterInactive: "bg-transparent text-muted-foreground border-border hover:bg-muted/50",
  };
}

interface Turn {
  speaker: string;
  sentences: AttributedSentence[];
}

function buildTurns(sentences: AttributedSentence[]): Turn[] {
  const sorted = [...sentences].sort((a, b) => a.char_start - b.char_start);
  const turns: Turn[] = [];
  let current: Turn | null = null;
  for (const s of sorted) {
    if (!current || current.speaker !== s.speaker) {
      current = { speaker: s.speaker, sentences: [s] };
      turns.push(current);
    } else {
      current.sentences.push(s);
    }
  }
  return turns;
}

export function SpeakerAttributionViewer({
  attributionStatus,
  attributionJson,
  autoAcceptThreshold = 85,
}: SpeakerAttributionViewerProps) {
  const [selectedSpeaker, setSelectedSpeaker] = useState<string | null>(null);

  useEffect(() => {
    setSelectedSpeaker(null);
  }, [attributionJson]);

  const attribution: AttributionResult | null = (() => {
    if (!attributionJson) return null;
    try {
      return JSON.parse(attributionJson);
    } catch {
      return null;
    }
  })();

  const isProcessing = attributionStatus === "processing";
  const hasFailed = attributionStatus === "failed";
  const hasAttribution = attributionStatus === "done" && attribution !== null;
  const neverRun = !attributionStatus;
  const unparseable = attributionStatus === "done" && attribution === null;

  if (isProcessing) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="speakers-processing-state">
        <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
          <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
        <h3 className="text-base font-medium text-foreground mb-2">Speaker Analysis In Progress</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          The speaker attribution pipeline is running. Check back shortly to see the labeled result.
        </p>
      </div>
    );
  }

  if (hasFailed) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="speakers-failed-state">
        <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-destructive" />
        </div>
        <h3 className="text-base font-medium text-foreground mb-2">Speaker Analysis Failed</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          The speaker attribution pipeline encountered an error and no result is available for this session.
        </p>
      </div>
    );
  }

  if (neverRun || unparseable) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="speakers-empty-state">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
          <Users className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-base font-medium text-foreground mb-2">No Speaker Analysis Yet</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          {unparseable
            ? "The stored speaker result could not be read. The data may be incomplete."
            : "Speaker attribution has not been run for this session. Use the Label Speakers button in the Transcript or Notes tab to begin."}
        </p>
      </div>
    );
  }

  if (!hasAttribution || !attribution) return null;

  const { summary } = attribution;
  const allTurns = buildTurns(attribution.sentences);

  const speakersInResult = Array.from(new Set(attribution.sentences.map((s) => s.speaker)));

  const visibleTurns = selectedSpeaker
    ? allTurns.filter((t) => t.speaker === selectedSpeaker)
    : allTurns;

  const filteredSentenceCount = visibleTurns.reduce((acc, t) => acc + t.sentences.length, 0);
  const filteredUncertainCount = visibleTurns.reduce(
    (acc, t) => acc + t.sentences.filter((s) => s.confidence < autoAcceptThreshold).length,
    0,
  );
  const totalTurns = allTurns.length;
  const hiddenTurns = totalTurns - visibleTurns.length;

  return (
    <div data-testid="speakers-result">
      <div className="mb-5 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2" data-testid="speakers-filter">
          <button
            onClick={() => setSelectedSpeaker(null)}
            className={`inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full border transition-all ${
              selectedSpeaker === null
                ? "bg-foreground text-background border-foreground"
                : "bg-transparent text-muted-foreground border-border hover:bg-muted hover:text-foreground"
            }`}
            data-testid="speakers-filter-all"
          >
            All
          </button>
          {speakersInResult.map((spk) => {
            const style = getSpeakerStyle(spk);
            const isActive = selectedSpeaker === spk;
            return (
              <button
                key={spk}
                onClick={() => setSelectedSpeaker(isActive ? null : spk)}
                className={`inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full border transition-all ${
                  isActive ? style.filterActive : style.filterInactive
                }`}
                data-testid={`speakers-filter-${spk.toLowerCase()}`}
              >
                {style.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/60 text-sm" data-testid="speakers-summary">
          <Info className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="text-muted-foreground">
            {selectedSpeaker ? (
              <>
                <span className={
                  getSpeakerStyle(selectedSpeaker).label === "Clinician"
                    ? "text-blue-600 dark:text-blue-400"
                    : getSpeakerStyle(selectedSpeaker).label === "Patient"
                    ? "text-green-600 dark:text-green-400"
                    : "text-amber-600 dark:text-amber-400"
                }>
                  {getSpeakerStyle(selectedSpeaker).label}
                </span>
                {": "}
                {filteredSentenceCount} sentence{filteredSentenceCount !== 1 ? "s" : ""} in {visibleTurns.length} turn{visibleTurns.length !== 1 ? "s" : ""}
                {hiddenTurns > 0 && (
                  <span className="text-muted-foreground/70">
                    {" · "}{hiddenTurns} turn{hiddenTurns !== 1 ? "s" : ""} hidden
                  </span>
                )}
                {filteredUncertainCount > 0 && (
                  <span className="text-amber-600 dark:text-amber-400">
                    {" · "}{filteredUncertainCount} uncertain
                  </span>
                )}
              </>
            ) : (
              <>
                {summary.totalSentences} sentence{summary.totalSentences !== 1 ? "s" : ""}
                {Object.entries(summary.speakerTotals).map(([spk, n]) => (
                  <span key={spk}>
                    {" · "}
                    <span className={getSpeakerStyle(spk).label === "Clinician" ? "text-blue-600 dark:text-blue-400" : getSpeakerStyle(spk).label === "Patient" ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"}>
                      {getSpeakerStyle(spk).label}
                    </span>
                    {": "}
                    {n}
                  </span>
                ))}
                {summary.needsHumanReviewCount > 0 && (
                  <span className="text-amber-600 dark:text-amber-400">
                    {" · "}{summary.needsHumanReviewCount} uncertain
                  </span>
                )}
              </>
            )}
          </span>
        </div>
      </div>

      <div className="space-y-3" data-testid="speakers-turns">
        {visibleTurns.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="speakers-no-turns">
            No turns found for {selectedSpeaker ? getSpeakerStyle(selectedSpeaker).label : "this speaker"}.
          </div>
        ) : (
          visibleTurns.map((turn, turnIdx) => {
            const style = getSpeakerStyle(turn.speaker);
            return (
              <div
                key={turnIdx}
                className={`pl-3 py-2 pr-3 rounded-r-md ${style.bubble}`}
                data-testid={`speakers-turn-${turnIdx}`}
              >
                <div className="mb-1">
                  <span
                    className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border ${style.badge}`}
                    data-testid={`speakers-label-${turnIdx}`}
                  >
                    {style.label}
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">
                  {turn.sentences.map((s, sIdx) => {
                    const uncertain = s.confidence < autoAcceptThreshold;
                    return (
                      <span key={sIdx}>
                        {sIdx > 0 ? " " : ""}
                        <span
                          className={uncertain ? "opacity-70" : ""}
                          title={uncertain ? `Confidence: ${s.confidence}% — may be uncertain` : undefined}
                          data-testid={uncertain ? `speakers-uncertain-${s.sentence_id}` : undefined}
                        >
                          {s.text}
                          {uncertain && (
                            <span className="text-amber-500 dark:text-amber-400 ml-0.5 text-xs">[?]</span>
                          )}
                        </span>
                      </span>
                    );
                  })}
                </p>
              </div>
            );
          })
        )}
      </div>

      {(selectedSpeaker ? filteredUncertainCount : summary.needsHumanReviewCount) > 0 && (
        <p className="text-xs text-muted-foreground mt-3" data-testid="speakers-legend">
          <span className="text-amber-500 dark:text-amber-400">[?]</span> marks sentences below the auto-accept confidence threshold
        </p>
      )}
    </div>
  );
}
